import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { SuccessEffect, FailureEffect } from '../../effects';
import { useMinigameSound } from '../../foundation/hooks/useMinigameSound';

/**
 * Template16: 存在の侵食（ExistenceErosion）
 * 強制転移時の捕食判定 - 「喰われている」恐怖を視覚化
 *
 * Premium Horror Design - 没入感重視の洗練されたビジュアル
 */

// 人体パーツの定義
const BODY_PARTS = {
  head: { id: 'head', name: '頭', damage: 25, priority: 6 },
  torso: { id: 'torso', name: '胴体', damage: 30, priority: 5 },
  leftArm: { id: 'leftArm', name: '左腕', damage: 15, priority: 3 },
  rightArm: { id: 'rightArm', name: '右腕', damage: 15, priority: 4 },
  leftLeg: { id: 'leftLeg', name: '左脚', damage: 15, priority: 1 },
  rightLeg: { id: 'rightLeg', name: '右脚', damage: 15, priority: 2 },
};

// 侵食された部位の位置データ
const EROSION_POSITIONS = {
  head: { x: 100, y: 55, particles: 8 },
  torso: { x: 100, y: 160, particles: 12 },
  leftArm: { x: 45, y: 145, particles: 6 },
  rightArm: { x: 155, y: 145, particles: 6 },
  leftLeg: { x: 72, y: 310, particles: 6 },
  rightLeg: { x: 128, y: 310, particles: 6 },
};

// プレミアムホラーシルエット
const HumanSilhouette = ({ erodedParts, pulsingPart, isAttacking, existenceLevel }) => {
  const getPartStyle = (partId) => {
    const isEroded = erodedParts.includes(partId);
    const isPulsing = pulsingPart === partId;

    if (isEroded) {
      return {
        fill: 'url(#voidGradient)',
        stroke: 'none',
        opacity: 0.15,
        filter: 'url(#erosionDistort)',
      };
    }
    if (isPulsing) {
      return {
        fill: 'url(#targetGradient)',
        stroke: '#8b0000',
        strokeWidth: 1.5,
        filter: 'url(#targetGlow)',
      };
    }
    return {
      fill: 'url(#bodyGradient)',
      stroke: 'rgba(139, 69, 69, 0.3)',
      strokeWidth: 0.5,
    };
  };

  return (
    <svg viewBox="0 0 200 400" style={{ width: '180px', height: '360px' }}>
      <defs>
        {/* 通常時のボディグラデーション - 骨のような色合い */}
        <linearGradient id="bodyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#d4c4b0" />
          <stop offset="50%" stopColor="#b8a898" />
          <stop offset="100%" stopColor="#9a8878" />
        </linearGradient>

        {/* ターゲット時のグラデーション - 深紅 */}
        <radialGradient id="targetGradient" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#dc2626">
            <animate attributeName="stop-color" values="#dc2626;#7f1d1d;#dc2626" dur="0.8s" repeatCount="indefinite" />
          </stop>
          <stop offset="100%" stopColor="#450a0a" />
        </radialGradient>

        {/* 侵食された部分 - 虚無 */}
        <radialGradient id="voidGradient" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#0a0a0a" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>

        {/* ターゲットのグロー */}
        <filter id="targetGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feFlood floodColor="#8b0000" floodOpacity="0.6" result="color" />
          <feComposite in="color" in2="blur" operator="in" result="glow" />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* 侵食のディストーション */}
        <filter id="erosionDistort">
          <feTurbulence type="fractalNoise" baseFrequency="0.03" numOctaves="4" result="noise" seed="5">
            <animate attributeName="baseFrequency" values="0.03;0.05;0.03" dur="3s" repeatCount="indefinite" />
          </feTurbulence>
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="8" xChannelSelector="R" yChannelSelector="G" />
        </filter>

        {/* 周囲の闇のグラデーション */}
        <radialGradient id="darknessGradient" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="transparent" />
          <stop offset="60%" stopColor="rgba(10,10,10,0.3)" />
          <stop offset="100%" stopColor="rgba(10,10,10,0.8)" />
        </radialGradient>

        {/* 侵食パーティクル */}
        <filter id="particleBlur">
          <feGaussianBlur stdDeviation="1" />
        </filter>
      </defs>

      {/* 背景の闘気・オーラ */}
      {isAttacking && (
        <g>
          <ellipse cx="100" cy="200" rx="95" ry="190" fill="none" stroke="#450a0a" strokeWidth="40" opacity="0.15">
            <animate attributeName="rx" values="95;85;95" dur="0.6s" repeatCount="indefinite" />
            <animate attributeName="ry" values="190;180;190" dur="0.6s" repeatCount="indefinite" />
          </ellipse>
          <ellipse cx="100" cy="200" rx="80" ry="170" fill="none" stroke="#7f1d1d" strokeWidth="15" opacity="0.2">
            <animate attributeName="rx" values="80;70;80" dur="0.4s" repeatCount="indefinite" />
            <animate attributeName="ry" values="170;160;170" dur="0.4s" repeatCount="indefinite" />
          </ellipse>
        </g>
      )}

      {/* 頭部 */}
      <ellipse
        cx="100" cy="55" rx="28" ry="35"
        {...getPartStyle('head')}
        style={{ transition: 'all 0.4s ease' }}
      />

      {/* 胴体 */}
      <path
        d="M 65 95 Q 60 90 70 85 L 130 85 Q 140 90 135 95 L 140 205 Q 140 215 125 215 L 75 215 Q 60 215 60 205 Z"
        {...getPartStyle('torso')}
        style={{ transition: 'all 0.4s ease' }}
      />

      {/* 左腕 */}
      <path
        d="M 60 100 Q 45 105 40 130 L 30 190 Q 25 200 30 205 L 40 202 Q 50 195 55 185 L 60 135 Q 58 110 60 100"
        {...getPartStyle('leftArm')}
        style={{ transition: 'all 0.4s ease' }}
      />

      {/* 右腕 */}
      <path
        d="M 140 100 Q 155 105 160 130 L 170 190 Q 175 200 170 205 L 160 202 Q 150 195 145 185 L 140 135 Q 142 110 140 100"
        {...getPartStyle('rightArm')}
        style={{ transition: 'all 0.4s ease' }}
      />

      {/* 左脚 */}
      <path
        d="M 75 215 L 70 320 Q 65 345 60 360 L 60 385 Q 60 392 68 392 L 85 392 Q 90 392 90 385 L 88 355 Q 88 335 85 320 L 90 215"
        {...getPartStyle('leftLeg')}
        style={{ transition: 'all 0.4s ease' }}
      />

      {/* 右脚 */}
      <path
        d="M 125 215 L 130 320 Q 135 345 140 360 L 140 385 Q 140 392 132 392 L 115 392 Q 110 392 110 385 L 112 355 Q 112 335 115 320 L 110 215"
        {...getPartStyle('rightLeg')}
        style={{ transition: 'all 0.4s ease' }}
      />

      {/* 侵食パーティクル - 欠損部位から立ち昇る黒い霧 */}
      {erodedParts.map((partId, idx) => {
        const pos = EROSION_POSITIONS[partId];
        return (
          <g key={partId}>
            {Array.from({ length: pos.particles }).map((_, i) => {
              const angle = (i / pos.particles) * Math.PI * 2;
              const radius = 15 + Math.random() * 10;
              const startX = pos.x + Math.cos(angle) * radius * 0.3;
              const startY = pos.y + Math.sin(angle) * radius * 0.3;
              const delay = i * 0.15;
              return (
                <circle
                  key={i}
                  cx={startX}
                  cy={startY}
                  r={2 + Math.random() * 2}
                  fill="#0a0a0a"
                  opacity="0.6"
                  filter="url(#particleBlur)"
                >
                  <animate
                    attributeName="cy"
                    values={`${startY};${startY - 40 - Math.random() * 30}`}
                    dur={`${2 + Math.random()}s`}
                    repeatCount="indefinite"
                    begin={`${delay}s`}
                  />
                  <animate
                    attributeName="opacity"
                    values="0.6;0"
                    dur={`${2 + Math.random()}s`}
                    repeatCount="indefinite"
                    begin={`${delay}s`}
                  />
                  <animate
                    attributeName="r"
                    values={`${2 + Math.random() * 2};${5 + Math.random() * 3}`}
                    dur={`${2 + Math.random()}s`}
                    repeatCount="indefinite"
                    begin={`${delay}s`}
                  />
                </circle>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
};

// 存在度ゲージ - プレミアムデザイン
const ExistenceGauge = ({ level, color }) => {
  return (
    <div style={{
      width: '100%',
      maxWidth: '280px',
      padding: '0 16px',
    }}>
      <div className="t16-gauge-track" style={{
        height: '6px',
        borderRadius: '3px',
        overflow: 'hidden',
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.5)',
      }}>
        <div style={{
          height: '100%',
          width: `${level}%`,
          background: level > 70
            ? 'linear-gradient(90deg, #4a5568, #718096)'
            : level > 40
              ? 'linear-gradient(90deg, #92400e, #b45309)'
              : 'linear-gradient(90deg, #7f1d1d, #991b1b)',
          borderRadius: '2px',
          transition: 'width 0.5s ease, background 0.5s ease',
          boxShadow: level <= 40 ? '0 0 8px rgba(153, 27, 27, 0.5)' : 'none',
        }} />
      </div>
      <div className="t16-gauge-labels" style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '6px',
        fontSize: '11px',
        /* P8-2a: "Crimson Text" は webfont 読込が無く local() 検査でも未インストール＝
           一度も解決実績のない死名のため除去（描画は従来どおり後続へフォールバック）。 */
        fontFamily: '"Noto Serif JP", serif',
        letterSpacing: '0.05em',
      }}>
        <span>消滅</span>
        <span style={{ color: level <= 40 ? '#991b1b' : 'rgba(180, 160, 150, 0.8)' }}>
          {level}%
        </span>
        <span>存在</span>
      </div>
    </div>
  );
};

const Template16ExistenceErosion = ({
  hostSubmit,
  hostConfig,
  registryId = 'minigame_existenceerosion_16',
  templateId = 16
}) => {
  // T11〜T14 と同じ configKey パターン: hostConfig 未指定時に `?? {}` が毎レンダー新オブジェクトに
  // なっても、文字列キー比較で resolvedConfig の同一性を保ち、再レンダーループを防ぐ
  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const config = hostConfig ?? {};
    const safeNumber = (value, fallback) => (Number.isFinite(value) ? value : fallback);
    return {
      title: config.title ?? '存在の侵食',
      instruction: config.instruction ?? '赤く光った瞬間に抵抗',
      totalWaves: safeNumber(config.totalWaves, 6),
      waveDurationMs: safeNumber(config.waveDurationMs, 2000),
      resistWindowMs: safeNumber(config.resistWindowMs, 800),
      countdownSec: safeNumber(config.countdownSec, 3),
      successMessage: config.successMessage ?? '耐えきった...軽傷で済んだ',
      partialMessage: config.partialMessage ?? '一部が侵食された...',
      failMessage: config.failMessage ?? '存在が喰われた...',
      maxErosions: safeNumber(config.maxErosions, 3),
    };
  }, [configKey]);

  const backgroundStyle = useMinigameBackground(hostConfig);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const initializedRef = useRef(false);

  const [gameState, setGameState] = useState('countdown');
  const [countdown, setCountdown] = useState(resolvedConfig.countdownSec);
  const [currentWave, setCurrentWave] = useState(0);
  const [erodedParts, setErodedParts] = useState([]);
  const [pulsingPart, setPulsingPart] = useState(null);
  const [isAttacking, setIsAttacking] = useState(false);
  const [canResist, setCanResist] = useState(false);
  const [resistSuccess, setResistSuccess] = useState(false);
  const [showEffect, setShowEffect] = useState(null);
  const [result, setResult] = useState(null);
  const [lastErodedPart, setLastErodedPart] = useState(null);
  const [screenShake, setScreenShake] = useState(false);
  const [heartbeatIntensity, setHeartbeatIntensity] = useState(0);

  const resistWindowRef = useRef(null);
  const waveTimeoutRef = useRef(null);

  // R2 Wave2C: 不穏音をグローバル音エンジンへ寄せる（mood は manifest 通り dread・mute 一元支配）。
  // 旧来は templateローカルの AudioContext を 1 個抱えてミュートを無視して鳴らし、さらにアンマウントで
  // close されず漏れていた。撤去して hook 経由に統一する。
  //  - heartbeat（待機/カウントダウン中のループ）→ heartbeat() ハンドル（存在度で bpm を切替）
  //  - erosion（侵食された瞬間の一発）→ warning（dread の低く重い注意音）
  //  - resist（抵抗成功の一発）→ select（軽い確定音）
  const { warning, select, heartbeat } = useMinigameSound('dread');
  const heartbeatHandleRef = useRef(null);

  // 残りの部位を取得
  const remainingParts = useMemo(() => {
    return Object.keys(BODY_PARTS).filter(part => !erodedParts.includes(part));
  }, [erodedParts]);

  // 存在度の計算
  const existenceLevel = useMemo(() => {
    const totalDamage = erodedParts.reduce((sum, partId) => sum + BODY_PARTS[partId].damage, 0);
    return Math.max(0, 100 - totalDamage);
  }, [erodedParts]);

  // 不穏なワンショット音（グローバル音エンジン経由）。
  //  - 'erosion'（侵食された瞬間）→ warning（dread の低く重い注意音）で「喰われた」一撃を表す
  //  - 'resist'（抵抗成功）→ select（軽い確定音）
  // heartbeat ループは別 useEffect で heartbeat() ハンドルが担うため、ここでは扱わない。
  const playEerieSound = useCallback((type) => {
    if (type === 'erosion') {
      warning();
    } else if (type === 'resist') {
      select();
    }
  }, [warning, select]);

  // 心臓の鼓動エフェクト（音＝グローバル音エンジンの heartbeat ハンドル / 視覚フラッシュ＝ローカル interval）。
  // 旧来は 1 本の interval で「音 + 視覚フラッシュ」を同時に駆動していた。音だけエンジンへ移し、
  // 視覚フラッシュ（heartbeatIntensity）は同じ周期のローカル interval に残してタイミングを不変に保つ。
  // 周期は存在度で 1200ms（>50）/ 800ms（≤50）。bpm = 60000 / 周期 → 50bpm / 75bpm に換算。
  useEffect(() => {
    if (gameState !== 'waiting' && gameState !== 'countdown') {
      if (heartbeatHandleRef.current) {
        heartbeatHandleRef.current.stop();
        heartbeatHandleRef.current = null;
      }
      return;
    }

    const periodMs = existenceLevel > 50 ? 1200 : 800;
    const bpm = 60000 / periodMs;
    if (!heartbeatHandleRef.current) {
      heartbeatHandleRef.current = heartbeat(bpm);
    } else {
      heartbeatHandleRef.current.setBpm(bpm);
    }

    // 視覚フラッシュは従来どおりローカル interval（音は鳴らさない）。
    const interval = setInterval(() => {
      setHeartbeatIntensity(1);
      setTimeout(() => setHeartbeatIntensity(0), 200);
    }, periodMs);

    return () => clearInterval(interval);
  }, [gameState, existenceLevel, heartbeat]);

  // カウントダウン
  useEffect(() => {
    if (gameState !== 'countdown') return;

    if (countdown <= 0) {
      setGameState('waiting');
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [gameState, countdown]);

  // 波の処理
  useEffect(() => {
    if (gameState !== 'waiting') return;

    if (currentWave >= resolvedConfig.totalWaves || erodedParts.length >= resolvedConfig.maxErosions || remainingParts.length === 0) {
      setGameState('result');
      return;
    }

    const startWave = () => {
      const targetPart = remainingParts[Math.floor(Math.random() * remainingParts.length)];
      setPulsingPart(targetPart);
      setIsAttacking(true);
      setResistSuccess(false);

      const resistStart = Math.random() * (resolvedConfig.waveDurationMs - resolvedConfig.resistWindowMs - 500) + 300;

      resistWindowRef.current = setTimeout(() => {
        setCanResist(true);

        setTimeout(() => {
          setCanResist(false);

          if (!resistSuccess) {
            setErodedParts(prev => [...prev, targetPart]);
            setLastErodedPart(targetPart);
            setScreenShake(true);
            playEerieSound('erosion');
            setTimeout(() => setScreenShake(false), 400);
          }

          setPulsingPart(null);
          setIsAttacking(false);
          setCurrentWave(prev => prev + 1);
        }, resolvedConfig.resistWindowMs);
      }, resistStart);
    };

    waveTimeoutRef.current = setTimeout(startWave, 600);

    return () => {
      if (waveTimeoutRef.current) clearTimeout(waveTimeoutRef.current);
      if (resistWindowRef.current) clearTimeout(resistWindowRef.current);
    };
  }, [gameState, currentWave, remainingParts, resolvedConfig, resistSuccess, erodedParts.length, playEerieSound]);

  // 抵抗ボタン押下
  const handleResist = useCallback(() => {
    if (!canResist || resistSuccess) return;

    setResistSuccess(true);
    setCanResist(false);
    playEerieSound('resist');

    setTimeout(() => {
      setPulsingPart(null);
      setIsAttacking(false);
      setCurrentWave(prev => prev + 1);
    }, 300);
  }, [canResist, resistSuccess, playEerieSound]);

  // 結果判定
  useEffect(() => {
    if (gameState !== 'result') return;

    let determinedResult;
    if (erodedParts.length === 0) {
      determinedResult = { type: 'success', message: resolvedConfig.successMessage };
      setShowEffect('success');
    } else if (erodedParts.length >= resolvedConfig.maxErosions) {
      determinedResult = { type: 'fail', message: resolvedConfig.failMessage };
      setShowEffect('fail');
    } else {
      determinedResult = { type: 'partial', message: resolvedConfig.partialMessage };
    }

    setResult(determinedResult);

    const timer = setTimeout(() => {
      submit(determinedResult.type, {
        erodedParts,
        existenceLevel,
        erosionCount: erodedParts.length,
        totalWaves: currentWave,
        message: determinedResult.message
      });
    }, showEffect ? 2000 : 1500);

    return () => clearTimeout(timer);
  }, [gameState, erodedParts, existenceLevel, resolvedConfig, submit, currentWave, showEffect]);

  // リセット（T11〜T14 と同じ initializedRef ガード: 初期化は初回マウント時のみ実行）
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setGameState('countdown');
    setCountdown(resolvedConfig.countdownSec);
    setCurrentWave(0);
    setErodedParts([]);
    setPulsingPart(null);
    setIsAttacking(false);
    setCanResist(false);
    setResistSuccess(false);
    setShowEffect(null);
    setResult(null);
    setLastErodedPart(null);
    resetSubmission();
  }, [resolvedConfig, resetSubmission]);

  // スタイル定義
  const containerStyle = {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    overflow: 'hidden',
    fontFamily: '"Noto Serif JP", Georgia, serif', /* P8-2a: 未読込の "Crimson Text" を除去 */
    ...(backgroundStyle ?? {}),
  };

  const overlayStyle = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background: `radial-gradient(ellipse at center,
      transparent 20%,
      rgba(10, 8, 8, ${0.4 + (1 - existenceLevel / 100) * 0.4}) 60%,
      rgba(5, 3, 3, ${0.7 + (1 - existenceLevel / 100) * 0.25}) 100%)`,
    transition: 'background 0.5s ease',
  };

  const attackOverlayStyle = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background: 'radial-gradient(ellipse at center, rgba(127, 29, 29, 0.15) 0%, transparent 70%)',
    opacity: isAttacking ? 1 : 0,
    transition: 'opacity 0.3s ease',
  };

  const headerStyle = {
    position: 'absolute',
    top: '16px',
    left: '16px',
    right: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 10,
  };

  const badgeStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px',
    borderRadius: '4px',
    fontSize: '13px',
    letterSpacing: '0.05em',
  };

  const resistButtonStyle = {
    position: 'relative',
    padding: canResist ? '20px 48px' : '16px 40px',
    borderRadius: '4px',
    fontWeight: '500',
    fontSize: canResist ? '18px' : '15px',
    fontFamily: '"Noto Serif JP", serif', /* P8-2a: 未読込の "Crimson Text" を除去 */
    letterSpacing: '0.1em',
    transition: 'all 0.2s ease',
    cursor: canResist ? 'pointer' : 'default',
    border: canResist
      ? '1px solid rgba(153, 27, 27, 0.8)'
      : resistSuccess
        ? '1px solid rgba(100, 100, 100, 0.5)'
        : '1px solid rgba(60, 50, 50, 0.4)',
    backgroundColor: canResist
      ? 'rgba(127, 29, 29, 0.9)'
      : resistSuccess
        ? 'rgba(60, 60, 60, 0.6)'
        : 'rgba(30, 25, 25, 0.8)',
    color: canResist
      ? '#fecaca'
      : resistSuccess
        ? 'rgba(160, 160, 160, 0.9)'
        : 'rgba(120, 100, 100, 0.6)',
    boxShadow: canResist
      ? '0 0 20px rgba(153, 27, 27, 0.4), inset 0 0 20px rgba(153, 27, 27, 0.2)'
      : 'none',
    transform: canResist ? 'scale(1.05)' : 'scale(1)',
  };

  return (
    <div
      className="t16-root"
      style={{
        ...containerStyle,
        animation: screenShake ? 't16shake 0.4s ease-in-out' : undefined,
      }}
    >
      {/* 暗いビネットオーバーレイ */}
      <div style={overlayStyle} />

      {/* 攻撃時の赤いオーバーレイ */}
      <div style={attackOverlayStyle} />

      {/* 心臓の鼓動に合わせた微妙な明滅 */}
      <div style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        backgroundColor: 'rgba(60, 20, 20, 0.05)',
        opacity: heartbeatIntensity,
        transition: 'opacity 0.15s ease',
      }} />

      {/* エフェクト */}
      {showEffect === 'success' && <SuccessEffect />}
      {showEffect === 'fail' && <FailureEffect />}

      {/* ヘッダー */}
      <div style={headerStyle}>
        <div className="t16-badge" style={badgeStyle}>
          <span className="t16-badge-accent">侵食</span>
          <span className="t16-wave-count" style={{ fontWeight: '600' }}>{currentWave}</span>
          <span style={{ opacity: 0.5 }}>/</span>
          <span>{resolvedConfig.totalWaves}</span>
        </div>

        <div className="t16-badge" style={badgeStyle}>
          <span>欠損</span>
          <span style={{
            fontWeight: '600',
            color: erodedParts.length >= resolvedConfig.maxErosions - 1 ? '#991b1b' : '#d4c4b0'
          }}>
            {erodedParts.length}
          </span>
          <span style={{ opacity: 0.5 }}>/</span>
          <span>{resolvedConfig.maxErosions}</span>
        </div>
      </div>

      {/* カウントダウン */}
      {gameState === 'countdown' && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 10,
          animation: 't16fadeIn 0.5s ease-out',
        }}>
          <p className="t16-countdown-label" style={{
            marginBottom: '24px',
            fontSize: '15px',
            letterSpacing: '0.15em',
          }}>
            侵食が始まる...
          </p>
          <div className="t16-countdown-digit" style={{
            fontSize: '72px',
            fontWeight: '300',
          }}>
            {countdown}
          </div>
        </div>
      )}

      {/* メインゲーム */}
      {(gameState === 'waiting' || gameState === 'result') && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 10,
          animation: 't16fadeIn 0.5s ease-out',
        }}>
          {/* タイトル */}
          {resolvedConfig.title && gameState !== 'result' && (
            <h2 className="t16-title" style={{
              fontSize: '18px',
              fontWeight: '400',
              marginBottom: '8px',
              letterSpacing: '0.2em',
            }}>
              {resolvedConfig.title}
            </h2>
          )}

          {/* 人型シルエット */}
          <div style={{
            position: 'relative',
            marginBottom: '20px',
          }}>
            <HumanSilhouette
              erodedParts={erodedParts}
              pulsingPart={pulsingPart}
              isAttacking={isAttacking}
              existenceLevel={existenceLevel}
            />

            {/* ターゲット警告 */}
            {pulsingPart && (
              <div style={{
                position: 'absolute',
                top: '-8px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(127, 29, 29, 0.9)',
                padding: '6px 16px',
                borderRadius: '2px',
                color: '#fecaca',
                fontSize: '12px',
                letterSpacing: '0.1em',
                animation: 't16pulse 0.8s ease-in-out infinite',
                border: '1px solid rgba(153, 27, 27, 0.6)',
              }}>
                {BODY_PARTS[pulsingPart].name}
              </div>
            )}
          </div>

          {/* 存在度ゲージ */}
          <ExistenceGauge level={existenceLevel} />

          {/* 欠損部位 */}
          {erodedParts.length > 0 && gameState !== 'result' && (
            <div style={{
              fontSize: '11px',
              color: 'rgba(153, 27, 27, 0.7)',
              marginTop: '12px',
              letterSpacing: '0.05em',
            }}>
              喪失: {erodedParts.map(p => BODY_PARTS[p].name).join('、')}
            </div>
          )}

          {/* 最後の欠損メッセージ */}
          {lastErodedPart && gameState !== 'result' && (
            <div style={{
              color: 'rgba(153, 27, 27, 0.8)',
              fontSize: '13px',
              marginTop: '8px',
              animation: 't16fadeIn 0.3s ease-out',
            }}>
              {BODY_PARTS[lastErodedPart].name}が喰われた...
            </div>
          )}

          {/* 抵抗ボタン */}
          {gameState === 'waiting' && (
            <button
              onClick={handleResist}
              disabled={!canResist && !resistSuccess}
              style={resistButtonStyle}
            >
              {canResist ? '抵抗' : resistSuccess ? '成功' : '待機'}
            </button>
          )}

          {/* 結果表示 */}
          {result && (
            <div style={{
              marginTop: '32px',
              textAlign: 'center',
              animation: 't16fadeIn 0.5s ease-out',
            }}>
              <p style={{
                fontSize: '20px',
                fontWeight: '400',
                letterSpacing: '0.15em',
                color: result.type === 'success'
                  ? 'rgba(160, 160, 160, 0.9)'
                  : result.type === 'fail'
                    ? 'rgba(153, 27, 27, 0.9)'
                    : 'rgba(180, 140, 80, 0.9)',
              }}>
                {result.message}
              </p>
              {result.type !== 'success' && (
                <p className="t16-result-sub" style={{
                  fontSize: '12px',
                  marginTop: '12px',
                }}>
                  存在度: {existenceLevel}% — 欠損: {erodedParts.length}箇所
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* 説明文 */}
      {gameState === 'waiting' && !pulsingPart && (
        <p className="t16-instruction" style={{
          fontSize: '12px',
          marginTop: '20px',
          zIndex: 10,
          letterSpacing: '0.1em',
        }}>
          {resolvedConfig.instruction}
        </p>
      )}

      <style>{`
        @keyframes t16fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes t16shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-6px); }
          20%, 40%, 60%, 80% { transform: translateX(6px); }
        }
        @keyframes t16pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
};

export default Template16ExistenceErosion;
